import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus, Trash2 } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { applicationsApi, type ApplicationListItem } from "@/api/applications"
import { ApiError } from "@/api/client"
import {
  type Interview,
  type InterviewStatus,
  type InterviewType,
  interviewsApi,
} from "@/api/interviews"
import { Calendar } from "lucide-react"
import { EmptyState } from "@/components/shared/empty-state"
import { CardListSkeleton } from "@/components/shared/loading-skeletons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { PageHeader } from "@/components/dashboard/page-header"

const TYPE_LABEL: Record<InterviewType, string> = {
  video: "Video",
  phone: "Telefon",
  onsite: "Yüz yüze",
}

const STATUS_LABEL: Record<InterviewStatus, string> = {
  scheduled: "Planlı",
  completed: "Tamamlandı",
  cancelled: "İptal",
  no_show: "Gelmedi",
}

export function InterviewsPage() {
  const [showForm, setShowForm] = React.useState(false)
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ["interviews"], queryFn: () => interviewsApi.list() })

  const updateStatus = useMutation({
    mutationFn: (vars: { id: string; status: InterviewStatus }) =>
      interviewsApi.update(vars.id, { status: vars.status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interviews"] }),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Güncellenemedi."),
  })

  const remove = useMutation({
    mutationFn: (id: string) => interviewsApi.remove(id),
    onSuccess: () => {
      toast.success("Görüşme silindi.")
      qc.invalidateQueries({ queryKey: ["interviews"] })
    },
  })

  return (
    <>
      <PageHeader
        title="Mülakatlar"
        description="Planlı ve geçmiş aday görüşmeleri."
        action={
          <Button onClick={() => setShowForm((v) => !v)}>
            <Plus className="size-4" /> Yeni görüşme
          </Button>
        }
      />
      <div className="space-y-4 p-4 sm:p-6">
        {showForm && (
          <NewInterviewForm
            onCancel={() => setShowForm(false)}
            onCreated={() => {
              setShowForm(false)
              qc.invalidateQueries({ queryKey: ["interviews"] })
            }}
          />
        )}

        {q.isLoading && <CardListSkeleton count={3} />}
        {!q.isLoading && q.data?.data.length === 0 && (
          <EmptyState
            icon={Calendar}
            title="Henüz planlı görüşme yok"
            description="Aday detayından mülakat planlayarak burada takip edebilirsiniz."
          />
        )}

        <div className="space-y-2">
          {q.data?.data.map((i) => (
            <InterviewRow
              key={i.id}
              interview={i}
              onStatus={(s) => updateStatus.mutate({ id: i.id, status: s })}
              onDelete={() => {
                if (confirm("Bu görüşmeyi silmek istediğine emin misin?")) remove.mutate(i.id)
              }}
            />
          ))}
        </div>
      </div>
    </>
  )
}

function InterviewRow({
  interview: i,
  onStatus,
  onDelete,
}: {
  interview: Interview
  onStatus: (s: InterviewStatus) => void
  onDelete: () => void
}) {
  const dt = new Date(i.scheduled_at)
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm">
        <div className="min-w-[160px]">
          <div className="font-medium">
            {new Intl.DateTimeFormat("tr-TR", {
              dateStyle: "medium",
              timeStyle: "short",
            }).format(dt)}
          </div>
          <div className="text-xs text-muted-foreground">{i.duration_minutes} dk · {TYPE_LABEL[i.type]}</div>
        </div>
        <div className="min-w-[180px] flex-1">
          {i.candidate ? (
            <Link to={`/dashboard/candidates/${i.candidate.id}`} className="font-medium hover:underline">
              {i.candidate.full_name}
            </Link>
          ) : (
            <span>—</span>
          )}
          <div className="text-xs text-muted-foreground">{i.job?.title}</div>
        </div>
        {i.location_or_link && (
          <a
            href={i.location_or_link}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary underline"
          >
            Link
          </a>
        )}
        <Select value={i.status} onValueChange={(v) => onStatus(v as InterviewStatus)}>
          <SelectTrigger className="h-8 w-[140px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(STATUS_LABEL) as InterviewStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge variant={i.status === "scheduled" ? "secondary" : "outline"}>
          {STATUS_LABEL[i.status]}
        </Badge>
        <Button variant="ghost" size="sm" onClick={onDelete} aria-label="Sil">
          <Trash2 className="size-4" />
        </Button>
      </CardContent>
    </Card>
  )
}

function NewInterviewForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void
  onCreated: () => void
}) {
  const [applicationId, setApplicationId] = React.useState("")
  const [date, setDate] = React.useState("")
  const [time, setTime] = React.useState("10:00")
  const [duration, setDuration] = React.useState(30)
  const [type, setType] = React.useState<InterviewType>("video")
  const [link, setLink] = React.useState("")
  const [notes, setNotes] = React.useState("")

  const apps = useQuery({
    queryKey: ["applications"],
    queryFn: () => applicationsApi.list(),
  })

  const create = useMutation({
    mutationFn: () =>
      interviewsApi.create({
        application_id: applicationId,
        scheduled_at: new Date(`${date}T${time}`).toISOString(),
        duration_minutes: duration,
        type,
        location_or_link: link || null,
        notes: notes || null,
      }),
    onSuccess: () => {
      toast.success("Görüşme oluşturuldu.")
      onCreated()
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Oluşturulamadı."),
  })

  return (
    <Card>
      <CardContent className="grid gap-3 p-4 md:grid-cols-2">
        <div className="space-y-1.5 md:col-span-2">
          <Label>Başvuru</Label>
          <Select value={applicationId} onValueChange={setApplicationId}>
            <SelectTrigger>
              <SelectValue placeholder="Aday başvurusu seçin" />
            </SelectTrigger>
            <SelectContent>
              {(apps.data?.data ?? []).map((a: ApplicationListItem) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.candidate.full_name} — {a.job.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Tarih</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Saat</Label>
          <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Süre (dk)</Label>
          <Input
            type="number"
            min={5}
            max={480}
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value) || 30)}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Tip</Label>
          <Select value={type} onValueChange={(v) => setType(v as InterviewType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="video">Video</SelectItem>
              <SelectItem value="phone">Telefon</SelectItem>
              <SelectItem value="onsite">Yüz yüze</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>Lokasyon / Link</Label>
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://meet.google.com/…" />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label>Notlar</Label>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="md:col-span-2 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel}>
            Vazgeç
          </Button>
          <Button
            onClick={() => create.mutate()}
            disabled={!applicationId || !date || create.isPending}
          >
            Oluştur
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
